import { Fragment, useRef, useState, useEffect } from "react";
import { WithContext as ReactTags } from "react-tag-input";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Select from "react-select";
import toast from "react-hot-toast";
import { privateAxios } from "../utils/axios";

const KeyCodes = {
  comma: 188,
  enter: 13,
  backspace: 8,
};
const delimiters = [KeyCodes.comma, KeyCodes.enter];

export default function AddPurchaseDetails({
  addSaleModalSetting,
  handlePageUpdate,
}) {
  const cancelButtonRef = useRef(null);

  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [allProductsRaw, setAllProductsRaw] = useState([]);
  const [loading, setLoading] = useState(false);

  const [purchase, setPurchase] = useState({
    supplier: "",
    memoNumber: "",
    purchaseByName: "",
    purchaseByPhone: "",
    reference: "",
    purchaseDate: new Date().toISOString().split("T")[0],
    products: [
      {
        _rowId: crypto.randomUUID(),
        product: "",
        selectedCategory: "",
        quantity: 1,
        purchasePrice: 0,
        skus: [],
        warrantyExpireDate: "",
      },
    ],
    subTotal: 0,
    discount: 0,
    vat: 0,
    totalAmount: 0,
  });

  /* ================= FETCH SUPPLIERS & PRODUCTS ================= */
  useEffect(() => {
    privateAxios
      .get("/suppliers/all")
      .then((res) => {
        if (res.data?.success) {
          setSuppliers(
            res.data.data.map((s) => ({
              value: s._id,
              label: `${s.storeName} (${s.name})`,
            })),
          );
        }
      })
      .catch(() => toast.error("Failed to fetch suppliers"));

    privateAxios
      .get("/products/all")
      .then((res) => {
        if (res.data?.success) {
          setAllProductsRaw(res.data.data);
          setProducts(
            res.data.data.map((p) => ({
              value: p._id,
              label: `${p.name} - ${p.category}`,
              price: p.purchasePrice || 0,
              category: p.category,
            })),
          );
        }
      })
      .catch(() => toast.error("Failed to fetch products"));
  }, []);

  useEffect(() => {
    const subTotal = purchase.products.reduce(
      (sum, item) => sum + item.quantity * item.purchasePrice,
      0,
    );

    const discountAmount = (subTotal * (purchase.discount || 0)) / 100;
    const vatAmount = purchase.vat || 0;

    const total = subTotal - discountAmount + vatAmount;

    setPurchase((p) => ({
      ...p,
      subTotal,
      totalAmount: Math.max(total, 0),
    }));
  }, [purchase.products, purchase.discount, purchase.vat]);

  /* ================= PRODUCT ROW HANDLERS ================= */

  const handleProductChange = (index, field, value) => {
    setPurchase((prev) => {
      const updatedProducts = [...prev.products];
      const current = updatedProducts[index];

      // 🚫 Prevent duplicate product selection
      if (field === "product") {
        const isDuplicate = prev.products.some(
          (p, i) => i !== index && p.product === value,
        );

        if (isDuplicate) {
          toast.error("This product is already added");

          return prev; // ⛔ stop update
        }

        const selected = products.find((p) => p.value === value);

        updatedProducts[index] = {
          ...current,
          product: value,
          purchasePrice: selected?.price || 0,
          skus: [],
          quantity: 0,
        };
      }
      // 📦 SKU change → auto quantity
      else if (field === "skus") {
        updatedProducts[index] = {
          ...current,
          skus: value,
          quantity: value.length,
        };
      }
      // 🧩 other fields
      else {
        updatedProducts[index] = {
          ...current,
          [field]: value,
        };
      }

      return { ...prev, products: updatedProducts };
    });
  };

  const addProductRow = () => {
    setPurchase((p) => ({
      ...p,
      products: [
        ...p.products,
        {
          _rowId: crypto.randomUUID(),
          product: "",
          selectedCategory: "",
          quantity: 1,
          purchasePrice: 0,
          skus: [],
          warrantyExpireDate: "",
        },
      ],
    }));
  };

  const removeProductRow = (index) => {
    setPurchase((p) => ({
      ...p,
      products: p.products.filter((_, i) => i !== index),
    }));
  };

  /* ================= VALIDATION ================= */
  const validate = () => {
    if (!purchase.supplier) return "Supplier is required";
    if (!purchase.purchaseDate) return "Purchase date is required";
    for (const [i, item] of purchase.products.entries()) {
      if (!item.product) return `Product in row ${i + 1} is required`;
      if (item.quantity <= 0) return `Quantity in row ${i + 1} must be > 0`;
      if (item.purchasePrice < 0) return `Price in row ${i + 1} must be >= 0`;
    }
    return null;
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    const error = validate();
    if (error) return toast.error(error);

    setLoading(true);
    try {
      const formattedData = {
        ...purchase,
        products: purchase.products.map(({ _rowId, selectedCategory, ...p }) => ({
          ...p,
          quantity: Number(p.quantity),
          purchasePrice: Number(p.purchasePrice),
        })),
      };
      const response = await privateAxios.post("/purchase", formattedData);
      if (response.data.success) {
        toast.success("Purchase added successfully!");
        handlePageUpdate();
        addSaleModalSetting();
      } else {
        toast.error(response.data.message || "Failed to add purchase");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error adding purchase");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={addSaleModalSetting}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-7xl h-[90vh] transform overflow-hidden rounded-2xl bg-white p-8 shadow-2xl transition-all flex flex-col">
              {/* <Dialog.Panel className="relative w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all"> */}
                {/* Close Button */}
                <button
                  type="button"
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                  onClick={addSaleModalSetting}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>

                <Dialog.Title className="text-2xl font-semibold mb-6">
                  Add Purchase
                </Dialog.Title>
             <div className="flex-1 overflow-y-auto pr-2">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                  className="space-y-6"
                >
                  <div className="space-y-5">
                    {/* Purchase Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Supplier */}
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                          Supplier
                        </label>
                        <Select
                          options={suppliers}
                          placeholder="Select Supplier"
                          onChange={(s) =>
                            setPurchase((p) => ({ ...p, supplier: s.value }))
                          }
                          className="react-select-container"
                        />
                      </div>

                      {/* Memo Number */}
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                          Memo Number
                        </label>
                        <input
                          value={purchase.memoNumber}
                          onChange={(e) =>
                            setPurchase((p) => ({
                              ...p,
                              memoNumber: e.target.value,
                            }))
                          }
                          placeholder="Enter memo number"
                          className="w-full h-11 rounded-xl border border-gray-300 px-4 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Purchase By Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Purchase By Name */}
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                          Purchaser Name
                        </label>
                        <input
                          value={purchase.purchaseByName || ""}
                          onChange={(e) =>
                            setPurchase((p) => ({
                              ...p,
                              purchaseByName: e.target.value,
                            }))
                          }
                          placeholder="Enter purchaser name"
                          className="w-full h-11 rounded-xl border border-gray-300 px-4 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Purchase By Phone */}
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                          Purchaser Phone
                        </label>
                        <input
                          value={purchase.purchaseByPhone || ""}
                          onChange={(e) =>
                            setPurchase((p) => ({
                              ...p,
                              purchaseByPhone: e.target.value,
                            }))
                          }
                          placeholder="Enter purchaser phone number"
                          className="w-full h-11 rounded-xl border border-gray-300 px-4 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    {/* Reference + Purchase Date Same Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Reference */}
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                          Reference
                        </label>
                        <input
                          value={purchase.reference || ""}
                          onChange={(e) =>
                            setPurchase((p) => ({
                              ...p,
                              reference: e.target.value,
                            }))
                          }
                          placeholder="Enter reference"
                          className="w-full h-11 rounded-xl border border-gray-300 px-4 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>

                      {/* Purchase Date */}
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                          Purchase Date
                        </label>
                        <input
                          type="date"
                          value={purchase.purchaseDate}
                          onChange={(e) =>
                            setPurchase((p) => ({
                              ...p,
                              purchaseDate: e.target.value,
                            }))
                          }
                          className="w-full h-11 rounded-xl border border-gray-300 px-4 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Products */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 bg-gray-50 p-2 rounded-xl">
                      <div className="col-span-6">Product</div>
                      <div className="col-span-2">
                        {" "}
                        Warranty Expire Date (Optional)
                      </div>
                      <div className="col-span-1">Qty</div>
                      <div className="col-span-1">Price</div>
                      <div className="col-span-1">Total</div>
                      <div className="col-span-1">Action</div>
                    </div>

                    {purchase.products.map((item, i) => (
                      <div
                        key={item._rowId}
                        className="grid grid-cols-12 gap-2 p-2 border rounded-xl items-center"
                      >
                        <div className="col-span-6 rounded-lg border border-gray-200 bg-white p-3 space-y-3 shadow-sm">
                          {/* Category Filter */}
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-600">
                              Category
                            </label>
                            <Select
                              options={[
                                { value: "", label: "All Categories" },
                                ...Array.from(
                                  new Set(products.map((p) => p.category).filter(Boolean))
                                ).map((cat) => ({ value: cat, label: cat })),
                              ]}
                              placeholder="All Categories"
                              value={
                                item.selectedCategory
                                  ? { value: item.selectedCategory, label: item.selectedCategory }
                                  : { value: "", label: "All Categories" }
                              }
                              onChange={(s) => {
                                setPurchase((prev) => {
                                  const updated = [...prev.products];
                                  updated[i] = {
                                    ...updated[i],
                                    selectedCategory: s.value,
                                    product: "",
                                    purchasePrice: 0,
                                    skus: [],
                                    quantity: 0,
                                  };
                                  return { ...prev, products: updated };
                                });
                              }}
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>

                          {/* Product Select */}
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-600">
                              Product
                            </label>
                            <Select
                              options={
                                item.selectedCategory
                                  ? products.filter((p) => p.category === item.selectedCategory)
                                  : products
                              }
                              placeholder="Select a product"
                              value={products.find((p) => p.value === item.product) || null}
                              onChange={(s) =>
                                handleProductChange(i, "product", s.value)
                              }
                              className="react-select-container"
                              classNamePrefix="react-select"
                            />
                          </div>

                          {/* SKU Tags */}
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-600">
                              SKUs
                            </label>

                            <div className="rounded-md border border-dashed border-gray-300 px-2 py-1 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition">
                              <ReactTags
                                tags={(item.skus || []).map((s) => ({
                                  id: s,
                                  text: s,
                                }))}
                                delimiters={delimiters}
                                handleAddition={(tag) => {
                                  const newSku = tag.text.trim();
                                  setPurchase((prev) => {
                                    const existingSkus = prev.products[i].skus || [];
                                    const isDuplicate = existingSkus.some(
                                      (sku) => sku.toLowerCase() === newSku.toLowerCase(),
                                    );
                                    if (isDuplicate) {
                                      toast.error(`SKU "${newSku}" already added`);
                                      return prev;
                                    }
                                    const updatedProducts = [...prev.products];
                                    const updatedSkus = [...existingSkus, newSku];
                                    updatedProducts[i] = {
                                      ...updatedProducts[i],
                                      skus: updatedSkus,
                                      quantity: updatedSkus.length,
                                    };
                                    toast.success(`SKU "${newSku}" added successfully`);
                                    return { ...prev, products: updatedProducts };
                                  });
                                }}
                                handleDelete={(idx) => {
                                  setPurchase((prev) => {
                                    const currentSkus = prev.products[i].skus || [];
                                    const removedSku = currentSkus[idx];
                                    const updatedSkus = currentSkus.filter((_, x) => x !== idx);
                                    const updatedProducts = [...prev.products];
                                    updatedProducts[i] = {
                                      ...updatedProducts[i],
                                      skus: updatedSkus,
                                      quantity: updatedSkus.length,
                                    };
                                    if (removedSku) toast.success(`SKU "${removedSku}" removed`);
                                    return { ...prev, products: updatedProducts };
                                  });
                                }}
                              />

                              {/* <ReactTags
                                tags={(item.skus || []).map((s) => ({
                                  id: s,
                                  text: s,
                                }))}
                                delimiters={delimiters}
                                handleAddition={(tag) =>
                                  handleProductChange(i, "skus", [
                                    ...(item.skus || []),
                                    tag.text,
                                  ])
                                }
                                handleDelete={(idx) =>
                                  handleProductChange(
                                    i,
                                    "skus",
                                    (item.skus || []).filter(
                                      (_, x) => x !== idx,
                                    ),
                                  )
                                }
                                placeholder="Scan or type SKU and press Enter"
                                inputFieldPosition="bottom"
                                autocomplete
                                allowUnique
                              /> */}
                            </div>

                            <p className="text-[11px] text-gray-400">
                              Tip: Barcode scanner supported
                            </p>
                          </div>
                        </div>

                        <input
                          type="date"
                          value={item.warrantyExpireDate}
                          onChange={(e) =>
                            handleProductChange(
                              i,
                              "warrantyExpireDate",
                              e.target.value,
                            )
                          }
                          className="col-span-2 h-10 border rounded px-2"
                        />

                        <input
                          type="number"
                          // min={1}
                          value={item.skus.length}
                          readOnly
                          onChange={(e) =>
                            handleProductChange(
                              i,
                              "quantity",
                              Number(e.target.value),
                            )
                          }
                          className="col-span-1 h-10 border rounded px-2"
                        />

                        <input
                          type="number"
                          min={0}
                          value={item.purchasePrice}
                          onChange={(e) =>
                            handleProductChange(
                              i,
                              "purchasePrice",
                              Number(e.target.value),
                            )
                          }
                          className="col-span-1 h-10 border rounded px-2 bg-gray-50"
                        />

                        <input
                          readOnly
                          value={item.quantity * item.purchasePrice}
                          className="col-span-1 h-10 border rounded px-2 bg-gray-100"
                        />

                        <button
                          type="button"
                          onClick={() => removeProductRow(i)}
                          className="text-red-600 hover:text-red-800 col-span-12 sm:col-span-1"
                        >
                          ✕
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addProductRow}
                      className="text-blue-600 font-semibold hover:underline"
                    >
                      ➕ Add Product
                    </button>
                  </div>

                  {/* Totals */}
                  {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                        Subtotal
                      </label>
                      <input
                        readOnly
                        value={purchase.subTotal}
                        className="w-full h-11 rounded-xl border px-4 bg-gray-100 text-gray-800 font-semibold"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                        Discount %
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={purchase.discount}
                        onChange={(e) =>
                          setPurchase((p) => ({
                            ...p,
                            discount: Number(e.target.value),
                          }))
                        }
                        className="w-full h-11 rounded-xl border px-4 focus:ring-1 focus:ring-blue-500"
                        placeholder="Discount %"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                        Total Amount
                      </label>
                      <input
                        readOnly
                        value={purchase.totalAmount}
                        className="w-full h-11 rounded-xl border-2 border-blue-500 px-4 bg-blue-50 text-blue-700 font-semibold"
                      />
                    </div>
                  </div> */}

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    {/* Subtotal */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                        Subtotal
                      </label>
                      <input
                        readOnly
                        value={purchase.subTotal}
                        className="w-full h-11 rounded-xl border px-4 bg-gray-100 text-gray-800 font-semibold"
                      />
                    </div>

                    {/* Discount % */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                        Discount %
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={purchase.discount}
                        onChange={(e) =>
                          setPurchase((p) => ({
                            ...p,
                            discount: Number(e.target.value),
                          }))
                        }
                        className="w-full h-11 rounded-xl border px-4 focus:ring-1 focus:ring-blue-500"
                        placeholder="Discount %"
                      />
                    </div>

                    {/* VAT Amount (Direct Input) */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                        VAT Amount
                      </label>
                      <input
                        type="number"
                        min={0}
                        value={purchase.vat || 0}
                        onChange={(e) =>
                          setPurchase((p) => ({
                            ...p,
                            vat: Number(e.target.value),
                          }))
                        }
                        className="w-full h-11 rounded-xl border px-4 focus:ring-1 focus:ring-blue-500"
                        placeholder="Enter VAT amount"
                      />
                    </div>

                    {/* Total Amount */}
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">
                        Total Amount
                      </label>
                      <input
                        readOnly
                        value={purchase.totalAmount}
                        className="w-full h-11 rounded-xl border-2 border-blue-500 px-4 bg-blue-50 text-blue-700 font-semibold"
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-4 mt-4">
                    <button
                      type="button"
                      onClick={addSaleModalSetting}
                      className="px-6 py-2 rounded-xl text-gray-600 hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2 rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70"
                    >
                      {loading ? "Processing..." : "Add Purchase"}
                    </button>
                  </div>
                </form>
              
              </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
